import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Row, Col, Alert, Table, InputGroup } from 'react-bootstrap';
import api from '../lib/api';

const emptyBrand = {
  name: '',
  slug: '',
  allowed_domains: '',
  fitment_enabled: false,
  fitment_field_order: '',
  intent_patterns: [],
  link_templates: [
    { link_type: 'keyword', template: '', active: true },
    { link_type: 'fitment', template: '', active: true },
    { link_type: 'alternate', template: '', active: true },
  ],
  active: true,
};

const LINK_LABEL = {
  keyword: 'Keyword Search',
  fitment: 'Fitment Search',
  alternate: 'Alternate Search',
};

const BrandRouterCard = () => {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/brand-router/');
      setBrands(res.data || []);
    } catch (e) {
      setMsg('Failed to load brand router config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({ ...emptyBrand });
  };

  const editBrand = (b) => {
    // ensure full link_templates set for editing convenience
    const ltMap = {};
    (b.link_templates || []).forEach((lt) => { ltMap[lt.link_type] = lt; });
    const fullLT = ['keyword','fitment','alternate'].map((t) =>
      ltMap[t] ? { ...ltMap[t] } : { link_type: t, template: '', active: true }
    );
    setEditing({ ...b, link_templates: fullLT });
  };

  const upsert = async () => {
    setMsg('');
    try {
      const res = await api.post('/brand-router/', editing);
      setMsg(res?.data?.message || 'Saved');
      await load();
      setEditing(null);
      setTimeout(() => setMsg(''), 1500);
    } catch (e) {
      const detail = e?.response?.data || e?.message || 'Save failed';
      setMsg(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  const updateLinkTemplate = (link_type, key, value) => {
    setEditing((prev) => {
      const next = { ...prev };
      const idx = next.link_templates.findIndex((l) => l.link_type === link_type);
      if (idx === -1) next.link_templates.push({ link_type, template: '', active: true });
      const i = idx === -1 ? next.link_templates.length - 1 : idx;
      next.link_templates[i][key] = value;
      return next;
    });
  };

  const addIntent = () => {
    setEditing((prev) => ({
      ...prev,
      intent_patterns: [...(prev.intent_patterns || []), { pattern: '', priority: 100 }],
    }));
  };

  const removeIntent = (i) => {
    setEditing((prev) => {
      const arr = [...prev.intent_patterns];
      arr.splice(i, 1);
      return { ...prev, intent_patterns: arr };
    });
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <Card className="shadow-sm p-3">
      <h5 className="mb-3">🛣️ Dynamic Brand Router</h5>

      {msg ? <Alert variant={msg.startsWith('Error') ? 'danger' : 'success'}>{msg}</Alert> : null}

      {!editing && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="m-0">Active Brands</h6>
            <Button size="sm" onClick={startNew}>New Brand</Button>
          </div>
          <Table size="sm" responsive hover>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Allowed Domains</th>
                <th>Fitment</th>
                <th>Fields (order)</th>
                <th>Intents</th>
                <th>Links</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 ? (
                <tr><td colSpan={9} className="text-center">No brands</td></tr>
              ) : brands.map((b) => (
                <tr key={b.id}>
                  <td><code>{b.slug}</code></td>
                  <td>{b.name}</td>
                  <td style={{maxWidth:240, whiteSpace:'pre-wrap'}}>{b.allowed_domains}</td>
                  <td>{b.fitment_enabled ? 'Yes' : 'No'}</td>
                  <td>{b.fitment_field_order}</td>
                  <td style={{maxWidth:260}}>
                    {(b.intent_patterns || []).map(p => (
                      <div key={p.id}><code>{p.pattern}</code> <small className="text-muted">({p.priority})</small></div>
                    ))}
                  </td>
                  <td style={{maxWidth:260}}>
                    {(b.link_templates || []).map(lt => (
                      <div key={lt.id}><strong>{LINK_LABEL[lt.link_type] || lt.link_type}</strong>: <code>{lt.template}</code></div>
                    ))}
                  </td>
                  <td>{b.updated_at ? new Date(b.updated_at).toLocaleString() : '-'}</td>
                  <td><Button size="sm" variant="outline-primary" onClick={() => editBrand(b)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {editing && (
        <>
          <h6 className="mt-3">Edit Brand</h6>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editing.name}
                  onChange={(e) => setEditing({...editing, name: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Slug</Form.Label>
                <Form.Control
                  value={editing.slug}
                  onChange={(e) => setEditing({...editing, slug: e.target.value.toLowerCase()})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Label>Allowed Domains (comma-separated)</Form.Label>
            <Form.Control
              value={editing.allowed_domains}
              onChange={(e) => setEditing({...editing, allowed_domains: e.target.value})}
              placeholder="e.g. kamsiparts.com,kamsiparts.kambi3pl.com"
            />
          </Form.Group>

          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Check
                type="switch"
                id="fitment-enabled"
                label="Fitment/Data Fields Enabled"
                checked={!!editing.fitment_enabled}
                onChange={(e) => setEditing({...editing, fitment_enabled: e.target.checked})}
              />
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Fitment Field Order (comma-separated keys)</Form.Label>
                <Form.Control
                  value={editing.fitment_field_order}
                  onChange={(e) => setEditing({...editing, fitment_field_order: e.target.value})}
                  placeholder="e.g. make,model,year,vehicleType,engineType"
                />
                <div className="small text-muted">
                  These keys are used to ask **exactly one** missing field at a time and to build fitment links.
                </div>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <h6 className="mt-2">Intent Patterns (regex)</h6>
          <div className="small text-muted mb-1">
            Lower priority number = matched earlier. Example: <code>\b(shock|wiper|filter)\b</code>
          </div>
          {(editing.intent_patterns || []).map((p, i) => (
            <Row className="g-2 align-items-end mb-2" key={i}>
              <Col md={9}>
                <Form.Group>
                  <Form.Label className="mb-1">Pattern</Form.Label>
                  <Form.Control
                    value={p.pattern}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditing(prev => {
                        const arr = [...prev.intent_patterns];
                        arr[i] = {...arr[i], pattern: val};
                        return {...prev, intent_patterns: arr};
                      });
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="mb-1">Priority</Form.Label>
                  <Form.Control
                    type="number"
                    value={p.priority}
                    onChange={(e) => {
                      const val = parseInt(e.target.value || '0', 10);
                      setEditing(prev => {
                        const arr = [...prev.intent_patterns];
                        arr[i] = {...arr[i], priority: val};
                        return {...prev, intent_patterns: arr};
                      });
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={1}>
                <Button variant="outline-danger" onClick={() => removeIntent(i)}>X</Button>
              </Col>
            </Row>
          ))}
          <Button size="sm" variant="secondary" className="mb-3" onClick={addIntent}>+ Add Intent</Button>

          <hr />

          <h6 className="mt-2">Link Templates</h6>
          <div className="small text-muted mb-2">
            Use <code>{'{query}'}</code> for keyword/alternate. Use the keys from your field order in braces for fitment,
            e.g. <code>{'{make}'}</code>, <code>{'{model}'}</code>, <code>{'{year}'}</code>, <code>{'{vehicleType}'}</code>, <code>{'{engineType}'}</code>.
            Unknown/missing params are omitted cleanly.
          </div>

          {['keyword','fitment','alternate'].map((lt) => {
            const row = (editing.link_templates || []).find(x => x.link_type === lt) || { link_type: lt, template: '', active: true };
            return (
              <Card className="p-2 mb-2" key={lt}>
                <Row className="g-2 align-items-end">
                  <Col md={3}><strong>{LINK_LABEL[lt]}</strong></Col>
                  <Col md={7}>
                    <Form.Control
                      placeholder={lt === 'keyword'
                        ? 'https://yourdomain/search?keyword={query}&page=1'
                        : lt === 'fitment'
                          ? 'https://yourdomain/product?make={make}&model={model}&year={year}&engineType={engineType}&vehicleType={vehicleType}&page=1'
                          : 'https://yourdomain/alt?search={query}'
                      }
                      value={row.template}
                      onChange={(e) => updateLinkTemplate(lt, 'template', e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Check
                      type="switch"
                      label="Active"
                      checked={!!row.active}
                      onChange={(e) => updateLinkTemplate(lt, 'active', e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}

          <Row className="g-2 mt-2">
            <Col md={3}>
              <Form.Check
                type="switch"
                label="Brand Active"
                checked={!!editing.active}
                onChange={(e) => setEditing({...editing, active: e.target.checked})}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button onClick={upsert}>Save</Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default BrandRouterCard;
